/**
 * Qué se le pregunta al servidor cuando alguien teclea, y cuándo.
 *
 * POR QUÉ ESTO EXISTE. La aplicación tenía CUATRO campos de búsqueda escritos a
 * mano y ninguno se comportaba como el otro: el de `FilterBar` esperaba 250 ms
 * y volvía a preguntar aunque el texto no hubiera cambiado, el de Tickets
 * esperaba 250 ms sobre estado local, el de Correo esperaba 300 ms con un
 * `useEffect` que pedía `eslint-disable`, y el de Escalamiento escribía la URL
 * EN CADA TECLA. Cuatro pantallas, cuatro respuestas a la misma pregunta, y la
 * cuenta la paga el servidor de la organización grande, que es la única donde
 * alguien necesita buscar.
 *
 * LAS CUATRO REGLAS viven acá, separadas de React, porque son las que se
 * olvidan y las que hay que poder probar con una tabla:
 *
 * 1. **Se espera a que la persona deje de escribir** (`RETARDO_BUSQUEDA`). Sin
 *    esto «Rosselot» son ocho consultas y siete se descartan.
 * 2. **No se pregunta por menos de `MINIMO_BUSQUEDA` caracteres.** Una letra
 *    sola calza con media organización: el servidor lee hasta su tope y lo que
 *    vuelve no es «lo que empieza con a», es lo que el tope dejó pasar. Mejor
 *    no preguntar — y decirlo, que es lo que hace `Buscador`.
 * 3. **No se repite una pregunta ya contestada.** Teclear y borrar hasta dejar
 *    el mismo texto no vuelve a salir a la red.
 * 4. **Limpiar es inmediato.** Eso es del componente: acá está sólo qué término
 *    queda (`''`), no cuánto se espera.
 *
 * LO QUE ESTE MÓDULO NO HACE, a propósito: ordenar respuestas que llegan tarde.
 * Todas las listas pasan por React Query y la clave de caché lleva los
 * parámetros adentro (`queryKeys.list(tenant, recurso, params)`), así que la
 * respuesta de «an» cae en la entrada de «an» y la pantalla dibuja la de «ana»:
 * una respuesta vieja no tiene dónde pisar a la nueva. Lo que sí hacía falta
 * era no dejar la lista en blanco entremedio, y eso es `placeholderData` en
 * cada consulta más el atenuado de la pantalla.
 *
 * Módulo PURO: sin React y sin red. Lo que decide se prueba con una tabla.
 */

/** Lo que se espera desde la última tecla antes de preguntarle al servidor. */
export const RETARDO_BUSQUEDA = 300;

/** Desde cuántos caracteres una búsqueda contesta algo y no un tope. */
export const MINIMO_BUSQUEDA = 2;

/**
 * Lo tecleado convertido en lo que se le pregunta al servidor.
 *
 * Los espacios de los bordes y los repetidos de adentro se van: «ana  soto» y
 * «ana soto » son la misma pregunta, y tratarlas como distintas es una consulta
 * de más por cada espacio suelto — con la regla 3, además, es la diferencia
 * entre no salir a la red y salir.
 *
 * Por debajo del mínimo la pregunta es `''` —la lista sin filtrar— y no el
 * texto corto. Es deliberado: dejar puesto el término anterior mientras el
 * campo muestra otra cosa deja una lista que contesta una pregunta que ya no se
 * está haciendo, que es la forma más silenciosa de mentir.
 */
export function terminoDeBusqueda(texto: string, minimo = MINIMO_BUSQUEDA): string {
  const limpio = texto.trim().replace(/\s+/g, ' ');
  return limpio.length < minimo ? '' : limpio;
}

/**
 * Si este término hay que mandarlo, o ya está contestado (regla 3).
 *
 * La comparación es contra el ÚLTIMO término que pasó por el campo, no contra
 * lo que se ve escrito: «ana» y «ana » son la misma pregunta.
 */
export function debeEnviar(siguiente: string, ultimo: string): boolean {
  return siguiente !== ultimo;
}

/**
 * Si el campo tiene que volver a copiar lo que dice la URL.
 *
 * La condición no es «difieren» sino «difiere de lo último que pasó por el
 * campo», y la diferencia importa: al borrar «ab» hasta «a» el término
 * confirmado vuelve a `''` —una letra no se pregunta, regla 2— y un
 * resincronizado a secas le borraría de la pantalla la «a» recién tecleada.
 * Con esta regla sólo mandan los cambios de AFUERA: Atrás, «Limpiar filtros»,
 * cambiar de vista guardada.
 */
export function debeResincronizar(valor: string, ultimo: string): boolean {
  return valor !== ultimo;
}

/**
 * Si lo tecleado todavía no llegó al servidor.
 *
 * Es lo que atenúa la lista: lo que se ve abajo contesta al término anterior.
 * Un texto corto NO deja nada pendiente —no se va a preguntar nunca— salvo que
 * haya que soltar un filtro anterior, que es justo lo que compara esta cuenta.
 */
export function hayBusquedaPendiente(
  texto: string,
  valor: string,
  minimo = MINIMO_BUSQUEDA,
): boolean {
  return terminoDeBusqueda(texto, minimo) !== valor;
}

/** Hay algo escrito y no alcanza el mínimo, así que no se preguntó nada. */
export function esBusquedaInsuficiente(texto: string, minimo = MINIMO_BUSQUEDA): boolean {
  const escrito = texto.trim();
  return escrito.length > 0 && escrito.length < minimo;
}
